import { useEffect, useRef } from "react";
import { useInView } from "framer-motion";

const mulberry32 = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let z = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  z = (z + Math.imul(z ^ (z >>> 7), 61 | z)) ^ z;
  return ((z ^ (z >>> 14)) >>> 0) / 4294967296;
};

export const ChaosCanvas = ({ progress }) => {
  const wrapRef = useRef(null);
  const ref = useRef(null);
  const inView = useInView(wrapRef, { margin: "240px" });

  useEffect(() => {
    if (!inView) return undefined;
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w = 0;
    let h = 0;
    let raf;

    const rand = mulberry32(7);
    const curves = Array.from({ length: 22 }, () => ({
      y1: rand(),
      y2: rand(),
      c1x: rand(),
      c1y: rand(),
      c2x: rand(),
      c2y: rand(),
      width: 0.6 + rand() * 1.1,
      alpha: 0.25 + rand() * 0.45,
    }));
    const dots = Array.from({ length: 40 }, () => ({ x: rand(), y: rand(), r: 1 + rand() * 2 }));

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const lerp = (a, b, t) => a + (b - a) * t;
    const smooth = (t) => t * t * (3 - 2 * t);

    const draw = () => {
      const e = smooth(Math.min(1, Math.max(0, progress.get())));
      ctx.clearRect(0, 0, w, h);

      curves.forEach((c, i) => {
        const gy = ((i + 1) / (curves.length + 1)) * h;
        const lift = Math.sin((i / curves.length) * Math.PI) * -h * 0.12;
        const p1y = lerp(c.y1 * h, gy, e);
        const p2y = lerp(c.y2 * h, gy, e);
        const c1x = lerp(c.c1x * w, w * 0.33, e);
        const c1y = lerp(c.c1y * h, gy + lift, e);
        const c2x = lerp(c.c2x * w, w * 0.66, e);
        const c2y = lerp(c.c2y * h, gy + lift, e);

        const r = Math.round(lerp(133, 5, e));
        const g = Math.round(lerp(148, 150, e));
        const b = Math.round(lerp(162, 105, e));
        ctx.strokeStyle = `rgba(${r},${g},${b},${lerp(c.alpha * 0.4, c.alpha, e)})`;
        ctx.lineWidth = c.width;
        ctx.beginPath();
        ctx.moveTo(0, p1y);
        ctx.bezierCurveTo(c1x, c1y, c2x, c2y, w, p2y);
        ctx.stroke();
      });

      dots.forEach((d, i) => {
        const cols = 10;
        const tx = (((i % cols) + 0.5) / cols) * w;
        const ty = ((Math.floor(i / cols) + 0.75) / 4.5) * h;
        const x = lerp(d.x * w, tx, e);
        const y = lerp(d.y * h, ty, e);
        ctx.fillStyle = `rgba(16,32,46,${0.06 + e * 0.2})`;
        ctx.beginPath();
        ctx.arc(x, y, d.r * (1 - e * 0.4), 0, Math.PI * 2);
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [inView, progress]);

  return (
    <div ref={wrapRef} className="h-full w-full">
      <canvas ref={ref} data-testid="chaos-clarity-canvas" className="h-full w-full" aria-hidden="true" />
    </div>
  );
};
